<script lang="ts">
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { Link } from '@inertiajs/svelte';
  import { auth } from '@/stores/user';
  import { page } from '@inertiajs/svelte';

  let {
    open = false,
    onclose
  }: {
    open?: boolean;
    onclose?: () => void;
  } = $props();

  const FAVORITE_BOARDS_KEY = 'favoriteBoards'
  let favoriteBoards: { slug: string; name: string }[] = $state([])
  let boardsExpanded = $state(true)

  // accessible / lock_reason 은 서버가 뷰어 기준으로 판정해 내려준다
  // (`ApplicationController#board_view_state`). 화면이 board_type 으로
  // 다시 유추하지 않는다 — 규칙이 두 벌이 되면 한쪽만 낡는다.
  interface Board { slug: string; name: string; board_type: string; accessible?: boolean; lock_reason?: string | null }
  let allBoards = $derived((page.props.boards as Board[]) || [])

  let isAuthenticated = $derived(Boolean($auth.user))
  let currentRole = $derived(String($auth.user?.role || ''))
  let isAdmin = $derived(['admin', 'manager', 'super_admin'].includes(currentRole))

  const adminHomeItem = { name: '관리자 홈', icon: 'admin', href: '/admin' };

  const adminGroups = [
    {
      key: 'operations',
      label: '운영',
      items: [
        { name: '사용자', icon: 'users', href: '/admin/users' },
        { name: '게시글', icon: 'posts', href: '/admin/posts' },
        { name: '댓글', icon: 'comments', href: '/admin/comments' },
        { name: '신고', icon: 'reports', href: '/admin/reports' },
      ]
    },
    {
      key: 'content',
      label: '콘텐츠',
      items: [
        { name: '기관', icon: 'organizations', href: '/admin/organizations' },
        { name: '게시판 관리', icon: 'boards', href: '/admin/boards' },
        { name: '카테고리', icon: 'categories', href: '/admin/categories' },
        { name: 'Threads', icon: 'threads', href: '/admin/threads_posts' },
      ]
    },
    {
      key: 'vibestudy',
      label: 'VibeStudy',
      items: [
        { name: '설문', icon: 'categories', href: '/admin/community/surveys' },
        { name: '팀', icon: 'users', href: '/admin/community/study_teams' },
        { name: '멤버', icon: 'users', href: '/admin/community/members' },
        { name: '댓글', icon: 'comments', href: '/admin/community/comments' },
      ]
    }
  ];

  let adminGroupOpen = $state<Record<string, boolean>>({});

  // Persistent layout에서 페이지 전환 시 currentPath 자동 업데이트
  let currentPath = $derived((page as any).url?.split('?')[0] || (typeof window !== 'undefined' ? window.location.pathname : '/'));
  let mounted = $state(false);

  $effect(() => {
    if (currentPath.startsWith('/boards')) {
      boardsExpanded = true;
    }
  });

  function isActive(href: string) {
    if (href === '/') return currentPath === '/';
    return currentPath.startsWith(href);
  }

  function handleLogout() {
    auth.clearUser();
    window.location.href = '/logout';
  }

  function getIcon(icon: string) {
    const icons: Record<string, string> = {
      dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
      users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      organizations: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      boards: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
      settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
      admin: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      posts: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      comments: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
      reports: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      categories: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
      threads: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
    };
    return icons[icon] || icons.dashboard;
  }

  function loadFavoriteBoards() {
    if (typeof window === 'undefined') return
    try {
      const saved = localStorage.getItem(FAVORITE_BOARDS_KEY)
      if (!saved) { favoriteBoards = []; return }
      const parsed = JSON.parse(saved)
      favoriteBoards = Object.values(parsed || {}) as { slug: string; name: string }[]
    } catch (error) {
      favoriteBoards = []
    }
  }

  onMount(() => {
    loadFavoriteBoards()
    window.addEventListener('favorite-boards:updated', loadFavoriteBoards)
    requestAnimationFrame(() => { mounted = true })
    return () => {
      window.removeEventListener('favorite-boards:updated', loadFavoriteBoards)
    }
  })
</script>

<!-- Mobile overlay -->
{#if open}
  <div
    class="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
    transition:fade={{ duration: 200 }}
    onclick={() => onclose?.()}
    onkeydown={(e) => e.key === 'Escape' && onclose?.()}
    role="button"
    tabindex="-1"
    aria-label="닫기"
  ></div>
{/if}

<!-- Sidebar -->
<aside
  class="fixed left-0 top-0 z-40 h-screen transform bg-surface border-r border-default/60 flex flex-col overflow-hidden
  {mounted ? 'transition-all duration-300 ease-out' : ''}
  {open ? 'w-[260px] translate-x-0' : 'w-0 -translate-x-full lg:w-0 lg:translate-x-0'}
  lg:relative lg:border-r"
>
  <!-- Sidebar header -->
  <div class="flex items-center justify-between px-5 h-16 border-b border-default/60 shrink-0">
    <Link href="/" class="flex items-center gap-2.5 group">
      <div class="w-8 h-8 bg-gradient-to-br from-primary to-primary-700 rounded-xl flex items-center justify-center shadow-sm">
        <span class="text-white font-bold text-xs tracking-tight">AX</span>
      </div>
      <span class="text-h2 text-content">Admin</span>
    </Link>
    <button
      onclick={() => onclose?.()}
      class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors cursor-pointer lg:hidden"
      aria-label="사이드바 닫기"
    >
      <svg class="h-5 w-5 text-content-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  <!-- Navigation items -->
  <nav class="flex-1 overflow-y-auto px-3 py-4" aria-label="주 메뉴">
    <ul class="space-y-1">
      <!-- 대시보드 -->
      <li>
        <Link
          href="/dashboard"
          class="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
            {isActive('/dashboard')
              ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300'
              : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
            }"
        >
          <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width={isActive('/dashboard') ? '2' : '1.5'} d={getIcon('dashboard')} />
          </svg>
          <span>대시보드</span>
          {#if isActive('/dashboard')}
            <div class="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></div>
          {/if}
        </Link>
      </li>

      <!-- 게시판 (펼침/접힘) -->
      <li>
        <button
          type="button"
          onclick={() => (boardsExpanded = !boardsExpanded)}
          class="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
            {currentPath.startsWith('/boards')
              ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300'
              : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
            }"
        >
          <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width={currentPath.startsWith('/boards') ? '2' : '1.5'} d={getIcon('boards')} />
          </svg>
          <span class="flex-1 text-left">게시판</span>
          <svg
            class="w-4 h-4 shrink-0 transition-transform duration-200 {boardsExpanded ? 'rotate-180' : ''}"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        {#if boardsExpanded}
          <ul class="mt-1 ml-3 pl-3 border-l border-default space-y-0.5">
            <li>
              <Link
                href="/boards"
                class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer
                  {currentPath === '/boards'
                    ? 'text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-content-tertiary hover:text-gray-900 dark:hover:text-dark-text-high hover:bg-gray-100 dark:hover:bg-dark-tertiary'
                  }"
              >
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
                <span>전체 보기</span>
              </Link>
            </li>
            {#each allBoards as board}
              <li>
                <Link
                  href={`/boards/${board.slug}`}
                  class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer
                    {currentPath === `/boards/${board.slug}`
                      ? 'text-primary-700 dark:text-primary-300 font-medium bg-primary-50 dark:bg-primary/10'
                      : 'text-content-tertiary hover:text-gray-900 dark:hover:text-dark-text-high hover:bg-gray-100 dark:hover:bg-dark-tertiary'
                    }"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-dark-text-disabled shrink-0
                    {currentPath === `/boards/${board.slug}` ? 'bg-primary dark:bg-primary' : ''}"></span>
                  <span class="truncate">{board.name}</span>
                  {#if board.accessible === false}
                    <span class="ml-auto relative group/lock shrink-0">
                      <svg class="w-3 h-3 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      <span class="pointer-events-none absolute bottom-full right-0 mb-1.5 hidden group-hover/lock:block z-50">
                        <span class="whitespace-nowrap rounded-lg bg-gray-800 dark:bg-dark-tertiary px-2.5 py-1.5 text-xs text-white dark:text-dark-text-high shadow-lg">
                          {board.lock_reason === 'company_verification'
                            ? '소속 인증을 마치면 열립니다'
                            : '접근 권한이 없는 게시판입니다'}
                        </span>
                      </span>
                    </span>
                  {:else if board.board_type === 'verified_only'}
                    <span class="ml-auto relative group/lock shrink-0">
                      <svg class="w-3 h-3 text-gray-400 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                      </svg>
                      <span class="pointer-events-none absolute bottom-full right-0 mb-1.5 hidden group-hover/lock:block z-50">
                        <span class="whitespace-nowrap rounded-lg bg-gray-800 dark:bg-dark-tertiary px-2.5 py-1.5 text-xs text-white dark:text-dark-text-high shadow-lg">
                          소속 인증 회원 전용 게시판입니다
                        </span>
                      </span>
                    </span>
                  {/if}
                </Link>
              </li>
            {/each}
            {#if allBoards.length === 0}
              <li class="px-3 py-2 text-xs text-gray-400 dark:text-dark-text-disabled">게시판 없음</li>
            {/if}
          </ul>
        {/if}
      </li>
    </ul>

    {#if isAuthenticated}
      <div class="my-3 border-t border-default/60"></div>
      <p class="px-3.5 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-disabled">
        내 공간
      </p>
      <ul class="space-y-1">
        <li>
          <Link
            href="/my-profile/editor/design"
            class="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
              {isActive('/my-profile/editor')
                ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300'
                : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
              }"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width={isActive('/my-profile/editor') ? '2' : '1.5'} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>프로필 에디터</span>
            {#if isActive('/my-profile/editor')}
              <div class="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></div>
            {/if}
          </Link>
        </li>
        <li>
          <Link
            href="/my-profile/presentations"
            class="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
              {isActive('/my-profile/presentations')
                ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300'
                : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
              }"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width={isActive('/my-profile/presentations') ? '2' : '1.5'} d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
            </svg>
            <span>발표자료 관리</span>
            {#if isActive('/my-profile/presentations')}
              <div class="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></div>
            {/if}
          </Link>
        </li>
      </ul>

      <div class="my-3 border-t border-default/60"></div>
      <ul class="space-y-1">
        <li>
          <Link
            href="/settings"
            class="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
              {isActive('/settings')
                ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300'
                : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
              }"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width={isActive('/settings') ? '2' : '1.5'} d={getIcon('settings')} />
            </svg>
            <span>설정</span>
            {#if isActive('/settings')}
              <div class="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></div>
            {/if}
          </Link>
        </li>
      </ul>
    {/if}

    <!-- Admin Menu Section -->
    {#if isAuthenticated && isAdmin}
      <div class="my-3 border-t border-default/60"></div>
      <p class="px-3.5 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-disabled">
        관리자
      </p>
      <ul class="space-y-1">
        <li>
          <Link
            href={adminHomeItem.href}
            class="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
              {currentPath === adminHomeItem.href
                ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300'
                : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
              }"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d={getIcon(adminHomeItem.icon)} />
            </svg>
            <span>{adminHomeItem.name}</span>
          </Link>
        </li>

        {#each adminGroups as group}
          {@const groupActive = group.items.some(i => isActive(i.href))}
          {@const isOpen = adminGroupOpen[group.key] ?? groupActive}
          <li>
            <button
              type="button"
              onclick={() => (adminGroupOpen[group.key] = !isOpen)}
              class="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200
                {groupActive
                  ? 'text-primary-700 dark:text-primary-300'
                  : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
                }"
            >
              <span class="flex-1 text-left">{group.label}</span>
              <svg
                class="w-4 h-4 shrink-0 transition-transform duration-200 {isOpen ? 'rotate-90' : ''}"
                fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {#if isOpen}
              <ul class="mt-1 ml-2 space-y-0.5 border-l border-default/40 pl-2">
                {#each group.items as item}
                  <li>
                    <Link
                      href={item.href}
                      class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200 cursor-pointer
                        {isActive(item.href)
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary/15 dark:text-primary-300 font-medium'
                          : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'
                        }"
                    >
                      <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width={isActive(item.href) ? '2' : '1.5'} d={getIcon(item.icon)} />
                      </svg>
                      <span>{item.name}</span>
                      {#if isActive(item.href)}
                        <div class="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></div>
                      {/if}
                    </Link>
                  </li>
                {/each}
              </ul>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}

    <!-- Favorite boards -->
    {#if favoriteBoards.length > 0}
      <div class="px-2 py-2">
        <p class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-text-disabled">
          즐겨찾기 스페이스
        </p>
        <ul class="space-y-1">
          {#each favoriteBoards.slice(0, 6) as board}
            <li>
              <Link
                href={`/boards/${board.slug}`}
                class="flex items-center gap-2.5 rounded-xl px-3.5 py-2 text-sm font-medium text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-tertiary hover:text-gray-900 dark:hover:text-dark-text-high transition-colors"
              >
                <svg class="w-4 h-4 text-warning shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <span class="truncate">{board.name}</span>
              </Link>
            </li>
          {/each}
        </ul>
      </div>
      <div class="my-3 border-t border-default/60"></div>
    {/if}

    {#if favoriteBoards.length === 0}
      <div class="my-3 border-t border-default/60"></div>
    {/if}

    {#if isAuthenticated}
      <button
        onclick={handleLogout}
        class="flex items-center gap-3 w-full rounded-xl px-3.5 py-2.5 text-sm font-medium text-error hover:bg-error-light dark:hover:bg-error/10 transition-all duration-200 cursor-pointer group"
        aria-label="로그아웃"
      >
        <svg class="w-5 h-5 shrink-0 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d={getIcon('logout')} />
        </svg>
        <span class="group-hover:font-semibold transition-all">로그아웃</span>
      </button>
    {/if}
  </nav>

  <!-- Sidebar footer -->
  <div class="shrink-0 border-t border-default/60 px-5 py-3">
    {#if isAuthenticated}
      <p class="text-caption text-gray-300 dark:text-dark-text-disabled">AX Hub Admin v0.1.0</p>
    {:else}
      <div class="space-y-2">
        <Link
          href="/login"
          class="flex items-center justify-center rounded-xl bg-primary px-3.5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-primary-dark"
        >
          로그인하기
        </Link>
        <Link
          href="/register"
          class="flex items-center justify-center rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 dark:border-dark-border dark:text-dark-text-medium dark:hover:bg-dark-tertiary"
        >
          회원가입
        </Link>
        <p class="text-caption text-gray-300 dark:text-dark-text-disabled">AX Hub v0.1.0</p>
      </div>
    {/if}
  </div>
</aside>
